/**
 * League Overview — every slate in the league, and everything that could be on them.
 *
 * TWO SCREENS THAT SHOULD ALWAYS HAVE BEEN ONE. `Keepers › Draftboard` was the full priced list
 * of every keepable player in the league; the commissioner's review queue was the ten
 * declarations. Reading either one alone answers half a question — "Chase costs a 2nd" without
 * "and Vishal has already declared him", or the reverse — so the two are laid over each other
 * here: the reference list, with each GM's declaration marked on it.
 *
 * ALL TEN SEATS, YOURS INCLUDED. Your card used to be left out on the reasoning that this sits
 * under the screen where you built your slate, so it was the same names twice on one page. That
 * reasoning was about the page and not about the question. The question this board answers is
 * "where does round 4 stand across the league", and a board that silently drops one of the ten
 * columns cannot answer it: the keeper count, the tax total and the round costs were all
 * league figures with a tenth of the league missing. Your card is first and marked `you`.
 *
 * THE ROUND SHOWN IS THE ROUND CHARGED. The Grid says what a keeper COSTS; 2015-III-7 says which
 * pick he is actually paid with, and the two part company whenever a GM has two keepers priced
 * into the same round or has traded the pick the cheaper one wanted. Printing the Grid cost
 * here made the board disagree with the draftboard next door — two of Vishal's men both read
 * R12 while only one of them can stand there. Every declared row is now run through the same
 * resolver the board uses, over the same pick ledger, so a row that moved shows the Grid price
 * struck through beside the round it was actually charged to. A franchise tag is priced at the
 * tagged player's own round before any of that (2025 P05 §1).
 *
 * PUBLIC, AND SUBMITTED ONLY. This used to be gated to commissioners. A submitted slate is public
 * the moment it is submitted — that is what makes the docket work — so there is nothing here the
 * league may not see, and the man most in need of "who has declared what" is the manager deciding
 * whether to spend a 3rd on somebody. A DRAFT slate is never shown: it is a manager still
 * thinking, and this screen would turn his working note into a leak. That holds for your own
 * card too — your draft is on the screen above, and showing it here would be the one row on the
 * board that is not a fact.
 *
 * NO SCROLLBARS. Every card grows to its content. A board that hides its own bottom half behind
 * ten separate scroll gutters is a board you cannot read across, which is the only thing this one
 * is for.
 */

import { useEffect, useMemo, useState } from 'react';
import keeperRows from '../data/keepers-2026.json';
import { LEAGUE_2026 } from '../domain/config.js';
import { assessLuxuryTax } from '../domain/luxuryTax.js';
import { assignKeeperPicks } from '../domain/draftOrder.js';
import { ownedRoundsFor } from '../domain/picks2026.js';
import { useOwnership } from './useOwnership.js';
import { gmOfTeam, managerShort } from '../domain/identity.js';
import { faab } from '../domain/faab.js';
import {
  chargeEntries, chargeSummary, taxCharges,
} from '../domain/luxuryCharge.js';
import { note } from './auditor.js';
import { GmBadge } from './GmBadge.js';
import { NflMark } from './NflChip.js';
import { GMS } from '../store/session.js';
import type { FaabEntry, GmId, KeeperDeclaration, LeagueStore } from '../store/types.js';

const SEASON = 2026;

interface Row {
  player: string; team: string; pos: string; cost: number | null;
  mandatory: boolean; farm: boolean; sleeper: boolean;
  tagCost: number | null;
}
const ROWS = keeperRows as unknown as Row[];

/** What each GM could keep, priced, cheapest pick first. A null cost is not keepable at all. */
const POOL: Record<string, Row[]> = (() => {
  const out: Record<string, Row[]> = {};
  for (const r of ROWS) {
    if (r.cost === null) continue;
    const who = gmOfTeam(r.team).toUpperCase();
    (out[who] ??= []).push(r);
  }
  /* Cost, then name — the same order the declaration screen lists a roster in, so the two read
     alike and neither hands the pick resolver an order the other would not have. */
  for (const list of Object.values(out)) {
    list.sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99) || a.player.localeCompare(b.player));
  }
  return out;
})();

/**
 * The slate every GM has before he does anything.
 *
 * A farm graduate's rookie year is mandatory (2013-III-1 §5) — not a choice anybody has — so a GM
 * holding one has a slate whether or not he has opened the screen. Counting him as undeclared
 * would make the board say a man has kept nobody when the rules have already kept somebody for
 * him. Derived here rather than written into his declaration, so it stays true if the rule moves.
 */
const MANDATORY: Record<string, string[]> = Object.fromEntries(
  Object.entries(POOL).map(([who, list]) => [who, list.filter((r) => r.mandatory).map((r) => r.player)]),
);

export function EverySlate(
  { store, you, charge }: {
    store: LeagueStore;
    you?: string;
    /**
     * THE CHARGE BAND, and it is only ever handed down from a screen that knows two things this
     * one does not: whether the reader may write the ledger, and whether declarations have shut.
     * Undefined for a manager, and undefined for a commissioner while keepers are still open —
     * a door nobody may open is never drawn.
     */
    charge?: { actor: string; locked: boolean };
  },
) {
  const [declarations, setDeclarations] = useState<KeeperDeclaration[]>([]);
  const [all, setAll] = useState(false);
  const [ledger, setLedger] = useState<FaabEntry[]>([]);
  const [arming, setArming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => store.declarations(SEASON).then((d) => { if (live) setDeclarations(d); });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'declarations') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  /*
   * THE LEDGER, because "what does he owe" and "what has he been charged" are different questions
   * and the band exists to show the gap between them. Read only where the band can appear: a
   * manager's League Overview does not need the FAAB log to draw ten slates.
   */
  const wantsLedger = Boolean(charge?.locked);
  useEffect(() => {
    if (!wantsLedger) return undefined;
    let live = true;
    const load = () => store.faab(SEASON).then((f) => { if (live) setLedger(f); }).catch(() => {});
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'faab') void load(); });
    return () => { live = false; off(); };
  }, [store, wantsLedger]);

  /**
   * WHO ACTUALLY OWNS WHICH PICK.
   *
   * The resolver charges a keeper to a pick at or before his cost round, so it cannot be handed a
   * flat 1..16: a GM who sold his 4th cannot pay anybody with it, and a GM holding two 9ths can
   * pay two men from round 9. Same derivation the draftboard and Draft tools use, from the same
   * ledger, because a board that disagrees with the board next door about who owns what is worse
   * than a board that shows nothing.
   */
  const { ownership } = useOwnership(store);
  const ownedRounds = useMemo(() => {
    const out: Record<string, number[]> = {};
    for (const g of GMS) {
      const who = String(g).toUpperCase();
      out[who] = ownedRoundsFor(who, ownership, LEAGUE_2026.draftRounds);
    }
    return out;
  }, [ownership]);

  const slates = useMemo(() => {
    const submitted = new Map(
      declarations.filter((d) => d.status === 'submitted').map((d) => [d.gm.toUpperCase(), d]),
    );
    /*
      YOUR SEAT SORTS FIRST, and is otherwise an ordinary card. Ordering rather than omitting: the
      one card you already know the contents of is still the card you compare the other nine
      against, and it is the only one whose absence changed the totals above it.
    */
    const mine = you?.toUpperCase();
    const seats = GMS.map((g) => String(g).toUpperCase());
    seats.sort((a, b) => (a === mine ? -1 : b === mine ? 1 : 0));

    return seats.map((who) => {
      const d = submitted.get(who);
      const forced = MANDATORY[who] ?? [];
      const declared = [...new Set([...forced, ...(d?.keepers.map((k) => k.player) ?? [])])];
      const pool = POOL[who] ?? [];
      const chosen = new Map(d?.keepers.map((k) => [k.player, k]) ?? []);

      /*
        WHAT HE IS CHARGED, in two steps, in the order the rules apply them.

        1. THE PRICE. The Grid (2019-II-1), unless he franchised the man, in which case it is the
           round the player himself occupies (2025 P05 §1) — the tag exempts him from the penalty,
           not from the draft.
        2. THE PICK. `assignKeeperPicks` walks collisions back the way the draftboard does,
           honouring an `elevatedTo` the GM chose himself, and never places anybody later than
           his price. A mandatory farm graduate is in this list whether or not his GM sent a
           slate, because the rules have already charged him.
      */
      const priceOf = (r: Row) =>
        (chosen.get(r.player)?.franchiseTagged && r.tagCost !== null ? r.tagCost : r.cost!);
      const declaredRows = pool.filter((r) => declared.includes(r.player));
      const { assigned } = assignKeeperPicks(
        declaredRows.map((r) => ({
          id: r.player,
          costRound: priceOf(r),
          preferred: chosen.get(r.player)?.elevatedTo ?? null,
        })),
        ownedRounds[who] ?? [],
      );

      const tax = assessLuxuryTax({
        teamId: who,
        declared: declared.map((player) => ({
          playerId: player,
          franchiseTagged: chosen.get(player)?.franchiseTagged,
        })),
      }, LEAGUE_2026);

      return {
        gm: who,
        mine: who === mine,
        /* "Submitted" means he sent one. A slate that exists only because the rules put a farm
           graduate on it is not a declaration, and the count at the top must not claim it is. */
        submitted: Boolean(d),
        declared: new Set(declared),
        pool,
        /** player -> the round he is actually paid with. */
        charged: assigned,
        /** player -> what he is priced at, tag included, before any collision walk-back. */
        priced: new Map(declaredRows.map((r) => [r.player, priceOf(r)])),
        tagged: new Set(declaredRows.filter((r) => chosen.get(r.player)?.franchiseTagged).map((r) => r.player)),
        tax: tax.taxFaab,
      };
    });
  }, [declarations, ownedRounds, you]);

  /*
    All three figures count the SAME set — the cards below them, which is now the whole league.

    An earlier version left your own card off the board but counted you in "1 of 10 submitted", so
    one figure described ten managers and the two beside it described nine. Putting the tenth card
    back is what makes the header honest; it is the same fix from the other end.
  */
  const submittedCount = slates.filter((s) => s.submitted).length;
  const keeperCount = slates.reduce((n, s) => n + s.declared.size, 0);
  const taxTotal = slates.reduce((n, s) => n + s.tax, 0);

  /*
   * WHAT THE LEDGER IS MISSING. Empty when nothing is owed, when everything owed has been taken,
   * and — because `charge` is undefined then — for everybody who cannot write it anyway.
   */
  const owing = useMemo(
    () => (charge?.locked
      ? taxCharges(
        slates.map((x) => ({ gm: x.gm as GmId, tax: x.tax, submitted: x.submitted })),
        ledger,
        SEASON,
      )
      : []),
    [charge?.locked, slates, ledger],
  );
  const sum = chargeSummary(owing);
  /* Charged means the ledger agrees with the assessment AND there was something to agree about. */
  const settled = Boolean(charge?.locked) && owing.length === 0 && taxTotal > 0;

  const runCharge = async () => {
    if (!charge || owing.length === 0) return;
    setBusy(true);
    setFailed(null);
    const at = new Date().toISOString();
    try {
      for (const e of chargeEntries(owing, SEASON, charge.actor as GmId, at)) {
        await store.saveFaab(e);
      }
      note(store, charge.actor, {
        kind: 'note',
        what: `Charged ${SEASON} luxury tax to ${owing.length} GM${owing.length === 1 ? '' : 's'}`,
        detail: Object.fromEntries(owing.map((r) => [r.gm, faab(r.owed)])),
      });
      setArming(false);
    } catch {
      /*
       * NAMED, NOT SWALLOWED. This writes to ten men's balances; a run that half-finished and said
       * nothing is the worst outcome available. Pressing again is safe — the ids are derived — so
       * the message says that rather than describing the error.
       */
      setFailed('Some of it did not save. Press again; already-charged GMs are skipped.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="everyslate">
      {/*
        ONE HEADER LINE. Title, the league's running totals, and the one control.

        The totals were three big display-sized tiles across the top of the card, which put
        "0 of 10 slates submitted" at the same weight as ten managers' actual slates below it —
        a scoreboard for the thing you scrolled past to reach the thing you came for. They are
        the same three numbers, set as a subtitle, so the cards are what the card is about.

        And the control is a real segmented switch rather than two stacked buttons, because it
        is one question with two answers, not two things to press.
      */}
      <div className="eshead-top">
        <div className="estitle">
          <h3 className="striphead">League Overview</h3>
          <p className="essub">
            <b>{submittedCount}</b> of {slates.length} submitted
            <span className="essep" aria-hidden="true">·</span>
            <b>{keeperCount}</b> keepers
            <span className="essep" aria-hidden="true">·</span>
            <b className={taxTotal > 0 ? 'warn' : ''}>{faab(taxTotal)}</b> tax
          </p>
        </div>

        <div className="esswitch" role="tablist" aria-label="How much to show">
          <button type="button" role="tab" aria-selected={!all}
            className={!all ? 'on' : ''} onClick={() => setAll(false)}>
            Declared
          </button>
          <button type="button" role="tab" aria-selected={all}
            className={all ? 'on' : ''} onClick={() => setAll(true)}>
            All eligible
          </button>
        </div>
      </div>

      {/*
        THE LUXURY TAX, AS MONEY RATHER THAN AS A FIGURE ON A CARD.
        Tim, 3 September: "once keepers lock, you can charge FAAB to GMs." Every slate on this
        board already carries its tax and the header already totals it; what was missing was the
        act that takes it. It lives here because this is the only screen that holds all ten
        assessments at once, which is the thing a commissioner has to be looking at to press it.

        ARMS, THEN ACTS — the same two-step the commissioner console uses, for the same reason: it
        touches ten men's balances and the armed sentence states what it is about to do in the
        present tense rather than asking whether he is sure.
      */}
      {owing.length > 0 && (
        <div className={`estax${arming ? ' armed' : ''}`}>
          {arming ? (
            <>
              <p className="estaxsay">
                Takes {faab(Math.abs(sum.total))} off {sum.gms} GM{sum.gms === 1 ? '' : 's'} now.
                Their balances change immediately and the log records you.
              </p>
              <div className="estaxacts">
                <button type="button" className="btn primary" disabled={busy}
                  onClick={() => void runCharge()}>
                  {busy ? 'Charging\u2026' : 'Charge it'}
                </button>
                <button type="button" className="btn" disabled={busy}
                  onClick={() => setArming(false)}>
                  Not yet
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="estaxwhat">
                {/*
                  The count is GMs whose ledger row is wrong, which after an unlock exception is
                  not the same as GMs who owe something. The verb changes with the direction.
                */}
                Luxury tax not charged to {sum.gms} GM{sum.gms === 1 ? '' : 's'}
              </span>
              <button type="button" className="btn" onClick={() => setArming(true)}>
                Charge {faab(Math.abs(sum.total))}
              </button>
            </>
          )}
          {failed && <p className="estaxbad">{failed}</p>}
        </div>
      )}

      {/* Said once, quietly, so a second commissioner does not go looking for the button. */}
      {settled && (
        <p className="estaxdone">Luxury tax charged — {faab(taxTotal)} across the league.</p>
      )}

      <div className="esgrid">
        {slates.map((s) => {
          const shown = all ? s.pool : s.pool.filter((r) => s.declared.has(r.player));
          return (
            <div key={s.gm} className={`escard${s.submitted ? ' in' : ''}${s.mine ? ' mine' : ''}`}>
              <div className="eshead">
                <GmBadge gm={s.gm} />
                <b>{managerShort(s.gm)}</b>
                {s.mine && <span className="chip">you</span>}
                {s.submitted
                  ? <span className="chip good">submitted</span>
                  : <span className="chip warning">not in</span>}
                <span className="mut escount">
                  {s.declared.size} · {s.tax > 0 ? faab(s.tax) : 'no tax'}
                </span>
              </div>
              <ul className="eslist">
                {shown.length === 0 && (
                  <li className="mut esempty">
                    {all ? 'Nobody on his roster is keepable.' : 'Nothing declared.'}
                  </li>
                )}
                {shown.map((r) => {
                  const on = s.declared.has(r.player);
                  /*
                    A DECLARED ROW SHOWS THE ROUND HE IS PAID WITH; an eligible-but-undeclared row
                    shows the Grid price, because there is no pick assignment to show until
                    somebody declares him. When the two differ the price stays visible, struck
                    through, so a reader can see the collision rather than just its result.
                  */
                  const price = s.priced.get(r.player) ?? r.cost!;
                  const round = on ? s.charged.get(r.player) : undefined;
                  const moved = round !== undefined && round !== price;
                  const tagged = s.tagged.has(r.player);
                  const why = !on ? `Round ${price} on the Grid (2019-II-1).`
                    : round === undefined
                      ? `No pick at or before Round ${price} left to pay with (2015-III-7).`
                      : moved
                        ? `Priced at Round ${price}${tagged ? ' under the franchise tag (2025 P05 §1)' : ''}`
                          + `, charged to Round ${round} to clear a collision (2015-III-7).`
                        : `Charged to Round ${round}`
                          + `${tagged ? ' under the franchise tag (2025 P05 §1).' : ' (2019-II-1).'}`;
                  return (
                    <li key={r.player} className={on ? 'on' : ''}>
                      {/*
                        THE BOARD'S OWN TWO BADGES, BESIDE THE NAME (Tim, 8 September: "change the
                        farm diamond markers to the purple F rounded corner squares we're using in
                        the draft. move from before the round cost to after their last name" — and
                        "The F (e.g. Javonte Williams) should similarly change to a green T").

                        A diamond and a bare letter were this screen's private vocabulary for two
                        facts the draft board has drawn as a purple `F` and a green `T` since 17a,
                        and a man who reads both screens on the same evening should not have to
                        learn the second alphabet. Beside the name, because that is what they are
                        about — sitting in front of the round they read as something about the
                        price, which is the one thing they are not.
                      */}
                      <span className="esname">{r.player}</span>
                      {r.mandatory && (
                        <span className="kbadge f"
                          title="Farm graduate — his rookie year is mandatory (2013-III-1 §5)">F</span>
                      )}
                      {tagged && (
                        <span className="kbadge t"
                          title="Franchise tag — priced at his own round (2025 P05 §1)">T</span>
                      )}
                      <NflMark player={r.player} />
                      <span className={`poschip ident pos-${r.pos}`}>{r.pos}</span>
                      <span className={`escost${moved ? ' moved' : ''}`} title={why}>
                        {moved && <s className="esbase">R{price}</s>}
                        {on && round === undefined ? '—' : `R${round ?? price}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="note">
        Submitted slates only, and the round shown on a declared keeper is the pick he is charged
        to (2015-III-7), not always the Grid price.
        {' '}<span className="kbadge f">F</span> marks a farm graduate whose rookie year is mandatory,
        so he is on the slate whether or not his GM has sent one.
        {' '}<span className="kbadge t">T</span> marks a franchise tag.
      </p>
    </section>
  );
}
